import React, { useEffect, useRef, ReactNode, HTMLAttributes } from "react";

export interface ShadowDOMProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  styles?: { [key: string]: string };
  styleUrls?: string[];
  currentTheme?: string;
  mode?: "open" | "closed";
  onShadowRootCreate?: (root: ShadowRoot) => void;
  onShadowRootRemove?: (root: ShadowRoot) => void;
  scripts?: string[];
  scriptDefer?: boolean;
}

const ShadowDOM: React.FC<ShadowDOMProps> = ({
  children,
  styles = {},
  styleUrls = [],
  currentTheme,
  mode = "open",
  onShadowRootCreate: onShadowRootCreate,
  onShadowRootRemove: onShadowRootRemove,
  scripts = [],
  scriptDefer = false,
  ...hostProps
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  let shadowRoot: ShadowRoot | null = null;

  useEffect(() => {
    if (hostRef.current) {
      shadowRoot = hostRef.current.attachShadow({ mode });

      if (typeof onShadowRootCreate === "function" && shadowRoot) {
        onShadowRootCreate(shadowRoot);
      }

      if (styles && currentTheme && styles[currentTheme]) {
        const styleEl = document.createElement("style");
        styleEl.textContent = styles[currentTheme];
        shadowRoot.appendChild(styleEl);
      }

      styleUrls.forEach((styleUrl) => {
        const linkEl = document.createElement("link");
        linkEl.rel = "stylesheet";
        linkEl.href = styleUrl;
        shadowRoot?.appendChild(linkEl);
      });

      let slotNames: string[] = [];
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child) && child.props.slot) {
          slotNames.push(child.props.slot);
        }
      });

      if (slotNames.length > 0) {
        slotNames.forEach((name) => {
          const slot = document.createElement("slot");
          slot.name = name;
          shadowRoot?.appendChild(slot);
        });
      } else {
        const slot = document.createElement("slot");
        shadowRoot?.appendChild(slot);
      }

      scripts.forEach((src) => {
        const scriptEl = document.createElement("script");
        scriptEl.src = src;
        scriptEl.defer = scriptDefer;
        shadowRoot?.appendChild(scriptEl);
      });
    }

    return () => {
      if (typeof onShadowRootRemove === "function" && shadowRoot) {
        onShadowRootRemove(shadowRoot);
      }
    };
  }, [
    styles,
    styleUrls,
    currentTheme,
    mode,
    onShadowRootCreate,
    onShadowRootRemove,
    children,
    scripts,
    scriptDefer,
    hostProps,
  ]);

  return (
    <div
      {...hostProps}
      ref={hostRef}
    >
      {children}
    </div>
  );
};

export { ShadowDOM };
